<script>
export default {
  inheritAttrs: false,
}
</script>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

/**
 * @typedef {object} ColorPickerProps
 * @property {string} [value=''] - The current color value.
 * @property {('hex'|'rgb'|'hsl')} [mode='hex'] - The color mode.
 */

/**
 * @type {ColorPickerProps}
 */
const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  mode: {
    type: String,
    default: 'hex',
  },
})

const emit = defineEmits(['input', 'change'])

// Using HSV for more accurate color picking (like iro.js)
const hue = ref(0)
const saturation = ref(100)
const value = ref(100) // Changed from lightness to value

const saturationValueRef = ref(null) // Changed from saturationLightnessRef
const hueSliderRef = ref(null)

const isDraggingSV = ref(false) // Changed from isDraggingSL
const isDraggingHue = ref(false)

// Function to parse HSL string (e.g., "hsl(120, 100%, 50%)")
function parseHslString(hslStr) {
  const result = hslStr.match(/hsl\((\d+),\s*([\d.]+)%,\s*([\d.]+)%\)/)
  if (result) {
    return {
      h: Number.parseInt(result[1], 10),
      s: Number.parseFloat(result[2]),
      l: Number.parseFloat(result[3]),
    }
  }
  return null
}

// Function to parse RGB string (e.g., "rgb(255, 0, 0)")
function parseRgbString(rgbStr) {
  const result = rgbStr.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (result) {
    return {
      r: Number.parseInt(result[1], 10),
      g: Number.parseInt(result[2], 10),
      b: Number.parseInt(result[3], 10),
    }
  }
  return null
}

function hexToRgb(hex) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, (m, r, g, b) => {
    return r + r + g + g + b + b
  })

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: Number.parseInt(result[1], 16),
        g: Number.parseInt(result[2], 16),
        b: Number.parseInt(result[3], 16),
      }
    : null
}

// Improved RGB to HSV conversion (more accurate than RGB to HSL for color picking)
function rgbToHsv(r, g, b) {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const diff = max - min

  let h = 0
  const s = max === 0 ? 0 : diff / max
  const v = max

  if (diff !== 0) {
    if (max === r) {
      h = ((g - b) / diff + (g < b ? 6 : 0)) / 6
    }
    else if (max === g) {
      h = ((b - r) / diff + 2) / 6
    }
    else if (max === b) {
      h = ((r - g) / diff + 4) / 6
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    v: Math.round(v * 100),
  }
}

// Convert HSL to HSV for compatibility
function hslToHsv(h, s, l) {
  s /= 100
  l /= 100

  const v = l + s * Math.min(l, 1 - l)
  const sNew = v === 0 ? 0 : 2 * (1 - l / v)

  return {
    h,
    s: Math.round(sNew * 100),
    v: Math.round(v * 100),
  }
}

// Convert HSV to HSL for output compatibility
function hsvToHsl(h, s, v) {
  s /= 100
  v /= 100

  const l = v * (1 - s / 2)
  const sNew = l === 0 || l === 1 ? 0 : (v - l) / Math.min(l, 1 - l)

  return {
    h,
    s: Math.round(sNew * 100),
    l: Math.round(l * 100),
  }
}

const currentColor = computed(() => {
  // Convert HSV to RGB first
  const rgbValue = hsvToRgb(hue.value, saturation.value, value.value)

  switch (props.mode) {
    case 'hsl':
    { const hslValue = hsvToHsl(hue.value, saturation.value, value.value)
      return `hsl(${hslValue.h}, ${hslValue.s}%, ${hslValue.l}%)` }
    case 'rgb':
      return `rgb(${rgbValue.r}, ${rgbValue.g}, ${rgbValue.b})`
    case 'hex':
    default:
      return rgbToHex(rgbValue.r, rgbValue.g, rgbValue.b)
  }
})

const saturationValueBackground = computed(() => {
  return `hsl(${hue.value}, 100%, 50%)`
})

// Improved HSV to RGB conversion
function hsvToRgb(h, s, v) {
  // Normalize s and v to the range [0, 1]
  s /= 100
  v /= 100

  // Chroma: The difference between the brightest and darkest colors.
  const c = v * s
  // An intermediate value used in the calculation.
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  // The amount to add to each component to match the brightness.
  const m = v - c

  let r = 0
  let g = 0
  let b = 0

  const hueSegment = Math.floor(h / 60) % 6

  switch (hueSegment) {
    case 0: r = c; g = x; b = 0; break
    case 1: r = x; g = c; b = 0; break
    case 2: r = 0; g = c; b = x; break
    case 3: r = 0; g = x; b = c; break
    case 4: r = x; g = 0; b = c; break
    case 5: r = c; g = 0; b = x; break
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  }
}

function rgbToHex(r, g, b) {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`
}

function handleSaturationValueMouseDown(e) {
  isDraggingSV.value = true
  updateSaturationValue(e)
}

function handleHueMouseDown(e) {
  isDraggingHue.value = true
  updateHue(e)
}

function handleMouseMove(e) {
  if (isDraggingSV.value)
    updateSaturationValue(e)

  if (isDraggingHue.value)
    updateHue(e)
}

function handleMouseUp() {
  isDraggingSV.value = false
  isDraggingHue.value = false
}

function updateSaturationValue(e) {
  if (!saturationValueRef.value) return

  const rect = saturationValueRef.value.getBoundingClientRect()

  // Clamp values to stay within bounds (like iro.js)
  const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
  const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top))

  saturation.value = Math.round((x / rect.width) * 100)
  value.value = Math.round(100 - (y / rect.height) * 100) // Inverted Y for value
}

function updateHue(e) {
  if (!hueSliderRef.value) return

  const rect = hueSliderRef.value.getBoundingClientRect()

  // Clamp values to stay within bounds
  const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top))

  hue.value = Math.round((y / rect.height) * 360)
}

const svCursorPosition = computed(() => {
  if (!saturationValueRef.value) return { left: '0px', top: '0px' }

  const rect = saturationValueRef.value.getBoundingClientRect()
  const width = rect.width || saturationValueRef.value.offsetWidth
  const height = rect.height || saturationValueRef.value.offsetHeight

  const x = (saturation.value / 100) * width
  const y = ((100 - value.value) / 100) * height

  // Ensure cursor stays within bounds
  const clampedX = Math.max(9, Math.min(width - 9, x)) // 9px = half cursor width
  const clampedY = Math.max(9, Math.min(height - 9, y))

  return {
    left: `${clampedX}px`,
    top: `${clampedY}px`,
  }
})

const hueCursorPosition = computed(() => {
  if (!hueSliderRef.value) return { top: '0px' }

  const rect = hueSliderRef.value.getBoundingClientRect()
  const height = rect.height || hueSliderRef.value.offsetHeight
  const y = (hue.value / 360) * height

  // Ensure cursor stays within bounds
  const clampedY = Math.max(9, Math.min(height - 9, y))

  return {
    top: `${clampedY}px`,
  }
})

onMounted(() => {
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
})

watch([hue, saturation, value], () => {
  emit('input', currentColor.value)
})

watch(() => props.value, (newValue) => {
  if (!newValue) return

  let newHsv = null

  if (props.mode === 'hsl') {
    const parsed = parseHslString(newValue)
    if (parsed) newHsv = hslToHsv(parsed.h, parsed.s, parsed.l)
  }
  else if (props.mode === 'rgb') {
    const parsedRgb = parseRgbString(newValue)
    if (parsedRgb) newHsv = rgbToHsv(parsedRgb.r, parsedRgb.g, parsedRgb.b)
  }
  else if (props.mode === 'hex') {
    const rgb = hexToRgb(newValue)
    if (rgb) newHsv = rgbToHsv(rgb.r, rgb.g, rgb.b)
  }

  if (newHsv) {
    // Check if the new HSV values are different from the current ones
    // to prevent an infinite loop with the other watcher.
    if (hue.value !== newHsv.h || saturation.value !== newHsv.s || value.value !== newHsv.v) {
      hue.value = newHsv.h
      saturation.value = newHsv.s
      value.value = newHsv.v
    }
  }
}, { immediate: true })
</script>

<template>
  <div class="flex gap-2 items-start">
    <!-- Saturation/Value Picker (Changed from Saturation/Lightness) -->
    <div
      ref="saturationValueRef"
      class="w-[160px] h-[160px] relative rounded-lg overflow-hidden cursor-crosshair"
      :style="{ backgroundColor: saturationValueBackground }"
      @mousedown="handleSaturationValueMouseDown"
    >
      <!-- Gradients for saturation and value (following iro.js approach) -->
      <div class="saturation-gradient" />
      <div class="value-gradient" />
      <!-- Circular cursor with proper bounds -->
      <div
        class="absolute w-[18px] h-[18px] border-[3px] border-white rounded-full transform -translate-x-1/2 -translate-y-1/2 pointer-events-none shadow-lg z-10"
        :style="svCursorPosition"
      />
    </div>

    <!-- Hue Slider -->
    <div
      ref="hueSliderRef"
      class="hue-slider w-[24px] h-[160px] relative rounded-full cursor-pointer"
      @mousedown="handleHueMouseDown"
    >
      <!-- Circular cursor with proper bounds -->
      <div
        class="absolute w-[18px] h-[18px] border-[3px] border-white rounded-full left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-lg pointer-events-none"
        :style="hueCursorPosition"
      />
    </div>
  </div>
</template>

<style>
.saturation-gradient, .value-gradient {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none; /* Important for mouse events to pass through */
}

.saturation-gradient {
  /* White to transparent (saturation) - iro.js approach */
  background: linear-gradient(to right, #fff, rgba(255,255,255,0));
}

.value-gradient {
  /* Transparent to black (value) - iro.js approach */
  background: linear-gradient(to bottom, rgba(0,0,0,0), #000);
}

.hue-slider {
  background: linear-gradient(to bottom,
    #ff0000 0%,
    #ffff00 16.66%,
    #00ff00 33.33%,
    #00ffff 50%,
    #0000ff 66.66%,
    #ff00ff 83.33%,
    #ff0000 100%
  );
}
</style>
